import React, { useEffect, useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Badge, Panel } from "../ui";

/** Card payments on/off. Off turns every buy button into an enquiry. */
export default function PaymentsPanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    adminApi.get("/admin/shop/payments").then((r) => setState(r.data)).catch(() => setState(null));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      await adminApi.put("/admin/shop/payments", { enabled });
      await load();
      toast.success(enabled ? "Card payments are on" : "Card payments paused", {
        description: enabled
          ? "Buy buttons are back and the Google feed will refill."
          : "Buy buttons now send an enquiry instead.",
      });
    } catch (e) {
      toast.error("Could not change that");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <CreditCard className="h-4 w-4" /> Card payments
        </span>
      }
      data-testid="admin-payments-panel"
    >
      {!state ? (
        <p className="text-sm text-[color:var(--nq-stone)]">Checking...</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {state.enabled ? (
              <Badge tone="green" data-testid="payments-state-badge">on - customers can check out</Badge>
            ) : (
              <Badge tone="gold" data-testid="payments-state-badge">paused - buy buttons send an enquiry</Badge>
            )}
            <Badge tone={state.stripe_mode === "live" ? "green" : "gold"}>
              Stripe {state.stripe_mode}
            </Badge>
            {!state.webhook_secret_set && <Badge tone="red">no webhook secret</Badge>}
          </div>

          <p className="mt-3 text-xs leading-relaxed text-[color:var(--nq-stone)]">
            While payments are paused the shop still shows every part and its price, but checkout is
            hidden and the Google Merchant feed empties out - Google won't list stock you can't buy.
            Both come back the moment you switch it on.
          </p>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => toggle(true)}
              disabled={busy || state.enabled}
              className="btn-green px-4 py-2 text-xs disabled:opacity-50"
              data-testid="payments-enable-button"
            >
              Turn payments on
            </button>
            <button
              onClick={() => toggle(false)}
              disabled={busy || !state.enabled}
              className="btn-outline-nq px-4 py-2 text-xs disabled:opacity-50"
              data-testid="payments-pause-button"
            >
              Pause payments
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}
